import { httpsCallable } from "firebase/functions";

import { functionsClient } from "./firebase";
import { isSafeSponsoredUrl } from "./sponsoredFeed";
import type { SponsoredFeedItem } from "../types/sponsored";

type CandidateRequest = {
  subject?: string;
};

type CandidatePayload = {
  deliveryId: string;
  campaignId: string;
  advertiserName: string;
  headline?: string;
  body: string;
  mediaUrl?: string;
  ctaLabel?: string;
  ctaUrl?: string;
};

type CandidateResponse = {
  items: CandidatePayload[];
};

type TrackingRequest = {
  deliveryId: string;
  campaignId: string;
};

function optionalText(value: unknown, maxLength: number) {
  if (typeof value !== "string") return undefined;
  const clean = value.trim();
  return clean.length > 0 && clean.length <= maxLength ? clean : undefined;
}

function parseCandidate(value: CandidatePayload): SponsoredFeedItem | null {
  const deliveryId = optionalText(value.deliveryId, 128);
  const campaignId = optionalText(value.campaignId, 128);
  const advertiserName = optionalText(value.advertiserName, 120);
  const body = optionalText(value.body, 3_000);
  if (!deliveryId || !campaignId || !advertiserName || !body) return null;

  const headline = optionalText(value.headline, 180);
  const mediaUrl = isSafeSponsoredUrl(value.mediaUrl) ? value.mediaUrl : undefined;
  const ctaUrl = isSafeSponsoredUrl(value.ctaUrl) ? value.ctaUrl : undefined;
  const ctaLabel = ctaUrl ? optionalText(value.ctaLabel, 80) : undefined;

  return {
    type: "sponsored",
    key: `sponsored:${deliveryId}`,
    deliveryId,
    campaignId,
    advertiserName,
    body,
    ...(headline ? { headline } : {}),
    ...(mediaUrl ? { mediaUrl } : {}),
    ...(ctaUrl && ctaLabel ? { ctaUrl, ctaLabel } : {}),
  };
}

export async function getSponsoredFeedCandidates(
  request: CandidateRequest
): Promise<SponsoredFeedItem[]> {
  const callable = httpsCallable<CandidateRequest, CandidateResponse>(
    functionsClient,
    "getSponsoredFeedCandidates"
  );
  const result = await callable(request);
  return Array.isArray(result.data?.items)
    ? result.data.items
        .map(parseCandidate)
        .filter((item): item is SponsoredFeedItem => item !== null)
    : [];
}

async function recordSponsoredEvent(
  name: "recordSponsoredImpression" | "recordSponsoredClick",
  request: TrackingRequest
) {
  const callable = httpsCallable<TrackingRequest, { ok: boolean }>(
    functionsClient,
    name
  );
  await callable(request);
}

export function recordSponsoredImpression(item: SponsoredFeedItem) {
  return recordSponsoredEvent("recordSponsoredImpression", item);
}

export function recordSponsoredClick(item: SponsoredFeedItem) {
  return recordSponsoredEvent("recordSponsoredClick", item);
}
